import { env } from '@eruvaka/web-pondlogs-base';
const isRecord = (value) => {
    return typeof value === 'object' && value !== null;
};
const normalizeLocationId = (value) => {
    if (typeof value !== 'string')
        return null;
    const normalized = value.trim();
    return normalized || null;
};
const normalizeText = (value) => {
    if (typeof value !== 'string')
        return null;
    const normalized = value.trim();
    return normalized || null;
};
const normalizeNumber = (value) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value !== 'string')
        return null;
    const parsed = Number.parseFloat(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
};
const readTextByKeys = (entry, keys) => {
    for (const key of keys) {
        const normalized = normalizeText(entry[key]);
        if (normalized)
            return normalized;
    }
    return null;
};
const readNumberByKeys = (entry, keys) => {
    for (const key of keys) {
        const normalized = normalizeNumber(entry[key]);
        if (normalized !== null)
            return normalized;
    }
    return null;
};
const readLocationIdFromRecord = (entry) => {
    return (normalizeLocationId(entry.location_id) ??
        normalizeLocationId(entry._id) ??
        normalizeLocationId(entry.id));
};
const formatFarmName = (id) => {
    if (!id)
        return 'Unknown Farm';
    const shortId = id.length > 8 ? id.slice(-8) : id;
    return `Farm ${shortId}`;
};
const readLocationName = (entry) => {
    const directLocationName = readTextByKeys(entry, [
        'location_label',
        'location_name',
        'locationName',
        'village_name',
        'city',
        'district',
    ]);
    if (directLocationName)
        return directLocationName;
    const placeParts = [
        readTextByKeys(entry, ['village', 'village_name']),
        readTextByKeys(entry, ['city', 'town']),
        readTextByKeys(entry, ['state', 'province']),
    ].filter((part) => Boolean(part));
    return placeParts.length > 0 ? placeParts.join(', ') : null;
};
const readPondsCount = (entry) => {
    return readNumberByKeys(entry, [
        'ponds_count',
        'pond_count',
        'total_ponds',
        'ponds',
        'number_of_ponds',
    ]);
};
const readAreaHectares = (entry) => {
    return readNumberByKeys(entry, [
        'area_hectares',
        'area_ha',
        'total_area_hectares',
        'total_area_ha',
        'farm_area_hectares',
        'farm_area_ha',
        'total_farm_area',
    ]);
};
export const readSessionFarms = () => {
    try {
        const raw = localStorage.getItem(env.sessionStorageKey);
        if (!raw)
            return { farms: [], selectedLocationId: null };
        const parsed = JSON.parse(raw);
        const user = parsed.user;
        if (!user)
            return { farms: [], selectedLocationId: null };
        const byId = new Map();
        if (Array.isArray(user.locations)) {
            user.locations.forEach((entry) => {
                const id = readLocationIdFromRecord(entry);
                if (!id)
                    return;
                const name = typeof entry.name === 'string'
                    ? entry.name
                    : typeof entry.title === 'string'
                        ? entry.title
                        : typeof entry.location_name === 'string'
                            ? entry.location_name
                            : formatFarmName(id);
                const locationName = readLocationName(entry);
                const pondsCount = readPondsCount(entry);
                const areaHectares = readAreaHectares(entry);
                byId.set(id, { id, name, locationName, pondsCount, areaHectares });
            });
        }
        if (Array.isArray(user.allowed_locations)) {
            user.allowed_locations.forEach((allowedLocation) => {
                const id = normalizeLocationId(allowedLocation) ??
                    (typeof allowedLocation === 'object' && allowedLocation !== null
                        ? readLocationIdFromRecord(allowedLocation)
                        : null);
                if (!id || byId.has(id))
                    return;
                const entryRecord = typeof allowedLocation === 'object' && allowedLocation !== null
                    ? allowedLocation
                    : null;
                byId.set(id, {
                    id,
                    name: formatFarmName(id),
                    locationName: entryRecord ? readLocationName(entryRecord) : null,
                    pondsCount: entryRecord ? readPondsCount(entryRecord) : null,
                    areaHectares: entryRecord ? readAreaHectares(entryRecord) : null,
                });
            });
        }
        const selectedLocationId = normalizeLocationId(user.location_id) ??
            normalizeLocationId(user.default_location_id) ??
            (isRecord(user.global_settings)
                ? normalizeLocationId(user.global_settings.default_location_id)
                : null) ??
            Array.from(byId.values())[0]?.id ??
            null;
        // Only add selectedLocationId to farms if we actually have farm data
        // Don't create phantom farms from just a location ID
        if (selectedLocationId && !byId.has(selectedLocationId) && byId.size > 0) {
            byId.set(selectedLocationId, {
                id: selectedLocationId,
                name: formatFarmName(selectedLocationId),
                locationName: null,
                pondsCount: null,
                areaHectares: null,
            });
        }
        const result = { farms: Array.from(byId.values()), selectedLocationId };
        return result;
    }
    catch (err) {
        console.error('readSessionFarms: error', err);
        return { farms: [], selectedLocationId: null };
    }
};
